import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import Swal from "sweetalert2";
import SearchableSelect from "../../components/common/SearchableSelect";
import { resolveAssetUrl } from "../../api/config";

const CertificatesManager = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventsForDate, setEventsForDate] = useState([]);
  const [results, setResults] = useState([]);
  const [, setLoadingEvents] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [certificateLogos, setCertificateLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [savingLogos, setSavingLogos] = useState(false);
  const [logoForm, setLogoForm] = useState({ name: "", file: null });
  const selectedEventObj = eventsForDate.find((e) => String(e.id) === String(selectedEvent));
  const convenorName = String(selectedEventObj?.convenor_name || "").trim();
  const activeCustomLogoCount = useMemo(
    () => certificateLogos.filter((logo) => !!logo.is_active).length,
    [certificateLogos]
  );

  // load dates' events using your /events-by-date endpoint
  const loadEventsByDate = useCallback(async () => {
    if (!selectedDate) {
      setEventsForDate([]);
      return;
    }
    try {
      setLoadingEvents(true);
      const res = await API.fetchEventsByDate(selectedDate);
      setEventsForDate(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load events for date", "error");
    } finally {
      setLoadingEvents(false);
    }
  }, [selectedDate]);

  async function handleDownloadCertificate(registrationId) {
    const result = await Swal.fire({
        title: 'Download certificate',
        html: '<p>Choose action:</p>',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Download (existing)',
        denyButtonText: 'Force regenerate',
    });

    if (result.dismiss === Swal.DismissReason.cancel) return;

    const regenerate = !!result.isDenied; // true when user chose Force regenerate

    try {
        const { blob, filename } = await API.downloadRegistrationCertificate(registrationId, {
          regenerate,
        });
        // Create a link to download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || `certificate_${registrationId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        Swal.fire("Downloaded", "Certificate downloaded", "success");
    } catch (err) {
        console.error("Download cert error:", err);
        Swal.fire("Error", err?.message || "Failed to download certificate", "error");
    }
    }

  const loadResults = useCallback(async () => {
    if (!selectedEvent) {
      setResults([]);
      return;
    }
    try {
      setLoadingResults(true);
      const res = await API.fetchCertificateResultsByEvent(selectedEvent);
      setResults(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load registrations/results", "error");
    } finally {
      setLoadingResults(false);
    }
  }, [selectedEvent]);

  const loadCertificateLogos = useCallback(async () => {
    try {
      setLoadingLogos(true);
      const rows = await API.fetchCertificateLogos();
      setCertificateLogos(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      setCertificateLogos([]);
      Swal.fire("Error", "Failed to load certificate logos", "error");
    } finally {
      setLoadingLogos(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) loadEventsByDate();
  }, [selectedDate, loadEventsByDate]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    loadCertificateLogos();
  }, [loadCertificateLogos]);

  const handleSingle = async (row) => {
    await handleDownloadCertificate(row.registration_id);
  };

  const handleBulk = async () => {
    if (!selectedEvent) {
      Swal.fire("Select event first");
      return;
    }
    if (!results.length) {
      Swal.fire("No registrations/results for this event");
      return;
    }
    try {
      await API.downloadEventCertificatesZip(selectedEvent);
      Swal.fire("Downloaded", "All certificates zip downloaded", "success");
    } catch (err) {
      console.error("Bulk cert download error:", err);
      Swal.fire("Error", err?.message || "Failed to generate/download certificates zip", "error");
    }
  };

  const handleLogoCreate = async (e) => {
    e.preventDefault();
    if (!logoForm.name.trim() || !logoForm.file) {
      Swal.fire("Missing data", "Please enter logo name and select an image", "warning");
      return;
    }

    try {
      setSavingLogos(true);
      const fd = new FormData();
      fd.append("name", logoForm.name.trim());
      fd.append("logo", logoForm.file);
      await API.createCertificateLogo(fd);
      setLogoForm({ name: "", file: null });
      await loadCertificateLogos();
      Swal.fire("Saved", "Logo added for certificates", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to add logo", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoToggleActive = async (logo) => {
    try {
      setSavingLogos(true);
      const fd = new FormData();
      fd.append("name", String(logo.name || ""));
      fd.append("is_active", logo.is_active ? "0" : "1");
      await API.updateCertificateLogo(logo.id, fd);
      await loadCertificateLogos();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to update logo status", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoDelete = async (logo) => {
    const result = await Swal.fire({
      title: "Delete logo?",
      text: `This will remove "${logo.name}" from certificate layouts.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    try {
      setSavingLogos(true);
      await API.deleteCertificateLogo(logo.id);
      await loadCertificateLogos();
      Swal.fire("Deleted", "Certificate logo removed", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to delete logo", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  const handleLogoReorder = async (logoId, direction) => {
    const current = Array.isArray(certificateLogos) ? [...certificateLogos] : [];
    const index = current.findIndex((row) => Number(row.id) === Number(logoId));
    if (index < 0) return;

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= current.length) return;

    const tmp = current[index];
    current[index] = current[swapIndex];
    current[swapIndex] = tmp;

    try {
      setSavingLogos(true);
      await API.reorderCertificateLogos(current.map((row) => row.id));
      await loadCertificateLogos();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to reorder logos", "error");
    } finally {
      setSavingLogos(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          🏅 Certificates Manager
        </h1>
        <p className="text-gray-500 mb-6">
          Choose date and event, check player results, and generate certificates.
        </p>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Additional Certificate Logos</h2>
          <p className="text-sm text-gray-600">
            Add custom logos and reorder them. Active logos are placed on certificates in this exact order.
            Active logos: <span className="font-semibold">{activeCustomLogoCount}</span>
          </p>

          <form onSubmit={handleLogoCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-700">Logo Name</label>
              <input
                type="text"
                value={logoForm.name}
                onChange={(e) => setLogoForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
                placeholder="Example: Govt Emblem"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-700">Logo Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2"
              />
            </div>
            <button
              type="submit"
              disabled={savingLogos}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {savingLogos ? "Saving..." : "Add Logo"}
            </button>
          </form>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-100 rounded-xl">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Order</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Logo</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-bold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loadingLogos ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">Loading logos...</td>
                  </tr>
                ) : certificateLogos.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">No additional logos added.</td>
                  </tr>
                ) : (
                  certificateLogos.map((logo, idx) => (
                    <tr key={logo.id}>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-700">#{idx + 1}</td>
                      <td className="px-3 py-2">
                        <img
                          src={resolveAssetUrl(logo.logo_path, { folder: "certificate-logos" })}
                          alt={logo.name || "logo"}
                          className="h-10 w-auto object-contain"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{logo.name}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${logo.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          {logo.is_active ? "Active" : "Hidden"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleLogoReorder(logo.id, "up")}
                            disabled={savingLogos || idx === 0}
                            className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-xs disabled:opacity-50"
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoReorder(logo.id, "down")}
                            disabled={savingLogos || idx === certificateLogos.length - 1}
                            className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-xs disabled:opacity-50"
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoToggleActive(logo)}
                            disabled={savingLogos}
                            className="px-2 py-1 rounded bg-amber-500 text-white text-xs disabled:opacity-50"
                          >
                            {logo.is_active ? "Hide" : "Show"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLogoDelete(logo)}
                            disabled={savingLogos}
                            className="px-2 py-1 rounded bg-red-600 text-white text-xs disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Date + Event selection */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-200 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Select Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedEvent("");
                  setResults([]);
                }}
              />
            </div>

            <div className="flex-1">
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Select Event
              </label>
              <SearchableSelect
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                disabled={!eventsForDate.length}
              >
                <option value="">-- Choose --</option>
                {eventsForDate.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title} • {ev.sport_name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          </div>
          {selectedEvent ? (
            <div className="text-sm text-gray-600 mt-2 space-y-2">
              <p>
                Convenor (from event): <span className="font-semibold">{convenorName || "-"}</span>
              </p>
              <div className="flex flex-wrap gap-6 items-center">
                <div className="flex items-center gap-2">
                  <span className="font-medium">District Logo:</span>
                  {selectedEventObj?.district_logo_path ? (
                    <img
                      src={resolveAssetUrl(selectedEventObj.district_logo_path, { folder: "districts" })}
                      alt="District logo"
                      className="h-10 object-contain"
                    />
                  ) : (
                    <span className="text-gray-500">Not added</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Sponsor:</span>
                  <span>{selectedEventObj?.sponsor_name || "Not added"}</span>
                  {selectedEventObj?.sponsor_logo ? (
                    <img
                      src={resolveAssetUrl(selectedEventObj.sponsor_logo, { folder: "events/sponsors" })}
                      alt={selectedEventObj?.sponsor_name || "Sponsor logo"}
                      className="h-10 object-contain"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              onClick={handleBulk}
              disabled={!selectedEvent || !results.length}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              📦 Generate All Certificates
            </button>
          </div>
        </div>

        {/* Results table */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              Players & Results
            </h2>
            {loadingResults && (
              <span className="text-sm text-gray-500">Loading...</span>
            )}
          </div>

          {(!results.length && !loadingResults) ? (
            <div className="p-8 text-center text-gray-500">
              {selectedEvent ? "No registrations/results for this event" : "Select date and event"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Game
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Time / Mark
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Certificate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {results.map((row, idx) => (
                    <tr key={`${row.registration_id || "reg"}_${row.game_id || "game"}_${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">
                          {row.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.classification_name || row.classification || "Unclassified"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.game_name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.result_time || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                        {row.result_position || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleSingle(row)}
                          className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 shadow-sm"
                        >
                          📄 Generate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CertificatesManager;
